import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { getClubRecordsWithAgeGroups, getMeetSummary } from 'api/clubApi';
import MetaTags from 'components/common/MetaTags';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import AgeResultPageHeader from 'components/admin/result/AgeResultPageHeader';
import AgeResultPageOptionButton from 'components/admin/result/AgeResultPageOptionButton';
import AgeViewTrackResultDataTable from 'components/admin/result/AgeViewTrackResultDataTable';
import { resultsToAgeViewEventTrackTableData } from 'services/admin/ResultService';
import { getAgeRoundLabel } from 'services/admin/EventService';
import { useAgeViewData } from 'hooks/useAgeViewData';
import routes from 'routes';
import useGroupLabels from 'hooks/useGroupLabels';
import { formatAgeViewGroup } from 'utils/groupLabel';

const AgeViewTrackResults = ({
  clubId,
  meetId,
  eventType,
  athleteAgeGroup,
  gender,
  roundType,
}) => {
  const groupLabels = useGroupLabels(clubId);
  const navigate = useNavigate();

  const { data, loading, error } = useAgeViewData(
    {
      clubID: clubId,
      meetID: meetId,
      eventType,
      athleteAgeGroup,
      gender,
      roundType,
    },
    false
  );

  const [meet, setMeet] = useState(null);
  const [fetchingMeet, setFetchingMeet] = useState(true);
  const [clubRecords, setClubRecords] = useState([]);
  const [fetchingClubRecords, setFetchingClubRecords] = useState(true);

  const fetchMeet = useCallback(async () => {
    try {
      const response = await getMeetSummary(clubId, meetId);
      setMeet(response.data);
    } catch (err) {
      console.log('[Results] Fetch Meets error: ', err);
    }
  }, [clubId, meetId]);

  const fetchClubRecords = useCallback(async () => {
    if (!clubId || !eventType || !gender || !athleteAgeGroup) {
      return;
    }
    const response = await getClubRecordsWithAgeGroups(
      clubId,
      eventType,
      athleteAgeGroup,
      gender
    ).catch((e) => {
      console.error(e);
      return null;
    });
    if (response) {
      setClubRecords(response.data);
    }
  }, [clubId, eventType, gender, athleteAgeGroup]);

  useEffect(() => {
    fetchMeet().then(() => setFetchingMeet(false));
  }, [fetchMeet]);

  useEffect(() => {
    fetchClubRecords().then(() => setFetchingClubRecords(false));
  }, [fetchClubRecords]);

  const tableRows = data?.results
    ? resultsToAgeViewEventTrackTableData(
        data.results,
        clubId,
        meetId,
        eventType,
        roundType,
        navigate
      )
    : [];

  const dataTypeOptions = (data?.metadata?.availableRoundTypes || []).map(
    (t) => ({ label: getAgeRoundLabel(t), type: t, value: t })
  );

  const handleOptionChange = (name, value) => {
    const next = dataTypeOptions.find((o) => o.value === value);
    if (!next) return;
    navigate(
      routes.admin.ageView.url(
        clubId,
        meetId,
        eventType,
        athleteAgeGroup,
        gender,
        next.type
      )
    );
  };

  // "100m U11 F" under "Age Results", or "100m Y6 F" under "Year Results" for a club that groups by year level.
  // A combined group's page (step 8) reads its name and range: "200m Junior (8-10) M".
  const { groupingBasis, words } = groupLabels;

  return (
    <>
      <MetaTags title="SplitFast | Results" />
      <AdminTablePageLayout loading={fetchingMeet || fetchingClubRecords || (loading && !data)}>
        <AgeResultPageHeader
          title={
            eventType && athleteAgeGroup && gender
              ? `${eventType} ${formatAgeViewGroup(groupingBasis, athleteAgeGroup, data?.metadata?.groupName)} ${gender}`
              : 'Results'
          }
          suffix={
            data?.metadata?.publishingStatus === 'Preliminary'
              ? 'Preliminary'
              : ''
          }
          viewLabel={`${words.value} Results`}
          clubRecords={clubRecords}
          eventType={eventType}
          meet={meet}
        />

        <AgeResultPageOptionButton
          meet={meet}
          clubId={clubId}
          viewType="age-view"
        />

        {error ? (
          <div style={{ textAlign: 'center' }}>
            {error?.response?.data?.error || 'Event not found in this meet'}
          </div>
        ) : tableRows.length ? (
          <AgeViewTrackResultDataTable
            dataTypeOptions={dataTypeOptions}
            optionValues={{ dataType: roundType }}
            handleOptionChange={handleOptionChange}
            results={tableRows}
          />
        ) : (
          <div style={{ textAlign: 'center' }}>
            {loading ? 'Loading results...' : 'No results found'}
          </div>
        )}
      </AdminTablePageLayout>
    </>
  );
};

AgeViewTrackResults.propTypes = {
  clubId: PropTypes.string,
  meetId: PropTypes.string,
  eventType: PropTypes.string,
  athleteAgeGroup: PropTypes.string,
  gender: PropTypes.string,
  roundType: PropTypes.string,
  isOpenEditPopup: PropTypes.bool,
};

export default AgeViewTrackResults;
